import { dataService } from '../../data';
import { ProductView } from "./product";
import { PreselectView } from "./preselect";

interface SearchResult {
    heuristic: number;
    candidate: ProductView | PreselectView;
}

export class SearchTool {

    constructor(private readonly _data: dataService){
        // nothing to do
    }
    
    /************************************/
    /* SUPPORT METHODS                  */
    /************************************/

    private _noacute(c: string): string {
        switch (c) {
            case 'á':
            case 'à':
            case 'ä':
                return 'a';
            case 'é':
            case 'è':
            case 'ë':
                return 'e';
            case 'í':
            case 'ì':
            case 'ï':
                return 'i';
            case 'ó':
            case 'ò':
            case 'ö':
                return 'o';
            case 'ú':
            case 'ù':
            case 'ü':
                return 'u';
        }
        
        return c;
    }
    
    private _noacute_word(chararray: string[]): string[] {
        const _chararray: string[] = [];
        for(let i=0; i < chararray.length; i++){
            _chararray.push(this._noacute(chararray[i]));
        }
        return _chararray;
    }
            
    private _levenshtein(a: string, b: string): number {
        const an = a ? a.length : 0;
        const bn = b ? b.length : 0;
        if (an === 0) return bn;
        if (bn === 0) return an;

        const matrix: number[][] = [];

        for (let i = 0; i <= bn; i++) {
            matrix[i] = [i];
        }

        for (let j = 0; j <= an; j++) {
            matrix[0][j] = j;
        }

        for (let i = 1; i <= bn; i++) {
            for (let j = 1; j <= an; j++) {
                if (b.charAt(i - 1) === a.charAt(j - 1)) {
                    matrix[i][j] = matrix[i - 1][j - 1];
                } else {
                    matrix[i][j] = Math.min(
                        matrix[i - 1][j - 1] + 1,
                        Math.min(matrix[i][j - 1] + 1, matrix[i - 1][j] + 1)
                    );
                }
            }
        }

        return matrix[bn][an];
    }

    private _is_similar_word(srchstr: string, compstr: string): number {
        const _w1str = this._noacute_word([...compstr]).join('');
        const _w2str = this._noacute_word([...srchstr]).join('');
        
        const _dist = this._levenshtein(_w1str, _w2str);
        return 100 - (_dist / Math.max(_w1str.length, _w2str.length) * 100);
    }
    
    private _find_substring(srchstr: string, compstr: string): number {
        const _srchstr = srchstr.toLowerCase();
        const _compstr = compstr.toLowerCase();

        const _nomrsrch = _srchstr.split('').map(this._noacute).join('');
        const _normcomp = _compstr.split('').map(this._noacute).join('');

        let _maxthreshold = 0;
        for (let i = 0; i <= _normcomp.length - _nomrsrch.length; i++) {
            const _sbcompare = _normcomp.substring(i, i + _nomrsrch.length);
            const _threshold = this._is_similar_word(_nomrsrch, _sbcompare);
            if (_threshold > _maxthreshold) {
                _maxthreshold = _threshold;
            }
        }

        return _maxthreshold;
    }

    private _product_to_string(product: ProductView | PreselectView): string {
        let _product_str = "";
        // provide results with parent names
        let _parent = product.parent;
        while (_parent){
            _product_str += (_parent.name?.tr() ?? '') + "|";
            _parent = _parent.parent;
        }

        // provide results with name or description (display text for active UI language)
        _product_str += product.name?.tr() ?? '';
        const _description = product.description?.tr() ?? '';
        if (_description){
            _product_str += "/" + _description;
        }

        // provide results with product options
        if ('categories' in product && product.categories){
            for(const _category of product.categories){
                for (const _option of _category.options){
                    _product_str += ";" + (_option.name?.tr() ?? '');
                }
            }    
        }
        return _product_str;
    }

    private _search_product(search: string, product: ProductView | PreselectView): number {
        return this._find_substring(search, this._product_to_string(product));
    }
    
    /************************************/
    /* PUBLIC METHODS                   */
    /************************************/
    
    doSearch(text: string, products: ProductView[], threshold = 0): Array <ProductView | PreselectView> {
        const _results: SearchResult[] = [];
        
        // add all candidates to the results array
        let _heuristic = 0;
        for(const _product of products){
            if (_product.isgroup){
                continue;
            }

            _heuristic = this._search_product(text, _product);
            if (_heuristic > threshold){
                _results.push({
                    heuristic: _heuristic,
                    candidate: _product
                });        
            }

            if (_product.selects) {
                for(const _direct of _product.selects){
                    _heuristic = this._search_product(text, _direct);
                    if (_heuristic > threshold){
                        _results.push({
                            heuristic: _heuristic,
                            candidate: _direct
                        });        
                    }    
                }
            }
        }
        
        // order the results array 
        _results.sort((a, b) => { 
            return (b.heuristic - a.heuristic); 
        }); 
        
        // return the match products
        const _products: Array <ProductView | PreselectView> = [];
        for(const _result of _results){
            _products.push(_result.candidate);
        }
        return _products;
    } 
}